// Local recording — composite the grid, mix the audio, stream it to disk.
//
// Replaces the getDisplayMedia path, which asked the user to screen-share their
// own window, captured no microphone, and accumulated the whole file in a JS
// array until the tab ran out of memory.
//
// The three pieces live next door: recording/compositor.ts (pixels),
// recording/audio-mixer.ts (sound), recording/sink.ts (bytes + codec choice).
// This hook is only lifecycle.

import { useCallback, useEffect, useRef, useState } from 'react'
import { createCompositor, type Compositor, type CompositorTile } from '../recording/compositor'
import { createAudioMixer, type AudioMixer } from '../recording/audio-mixer'
import {
  createMp4FileStream,
  createRecordingSink,
  defaultRecordingName,
  isFallbackWebm,
  pickRecordingMimeType,
  supportedRecordingTypes,
  supportsFileStreaming,
  type RecordingSink,
} from '../recording/sink'
import { createMp4Recorder, supportsWebCodecsRecording, type Mp4Recorder } from '../recording/mp4-recorder'
import { vclog } from './vc-log'
import { isWeakForRecording } from '@/features/devices/services/device-capability'
import { useMeetingPreferencesStore } from '../store/meeting-preferences.store'

/** Chunk cadence. Big enough to keep writes cheap, small enough that a crash
 *  costs a few seconds rather than the whole recording. */
const TIMESLICE_MS = 3000

/** Encode cost scales with pixels × fps; nobody watches a recording at 1080p60. */
// 20 rather than 24: a meeting recording is not sport, and every frame is a
// composite plus an encode on the same thread the call runs on. The compositor
// backs off further on its own if frames turn out expensive.
const FULL = { width: 1280, height: 720, fps: 20 }
const WEAK = { width: 960, height: 540, fps: 12 }

export type LocalRecordingState = {
  isRecording: boolean
  isPaused: boolean
  durationSeconds: number
  /** Destination filename once started. */
  fileName: string | null
  error: string | null
  /**
   * Set when this browser could only give us WebM. Windows' built-in players
   * handle WebM badly — the caller should say so rather than let someone
   * discover it when the file won't play.
   */
  playbackWarning: string | null
}

type StartArgs = {
  /** Grid order — pass the same list the layout renders. */
  tiles: CompositorTile[]
  /** Every stream whose audio belongs in the file: local mic + remote peers. */
  audioStreams: MediaStream[]
}

export function useLocalRecording(onStateChange?: (recording: boolean) => void) {
  const [state, setState] = useState<LocalRecordingState>({
    isRecording: false,
    isPaused: false,
    durationSeconds: 0,
    fileName: null,
    error: null,
    playbackWarning: null,
  })

  const recorderRef = useRef<MediaRecorder | null>(null)
  const mp4Ref = useRef<Mp4Recorder | null>(null)
  const compositorRef = useRef<Compositor | null>(null)
  const mixerRef = useRef<AudioMixer | null>(null)
  const sinkRef = useRef<RecordingSink | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // Writes are async and MediaRecorder does not wait; chaining keeps chunks in
  // order and lets stop() await the last one before closing the file.
  const writeChainRef = useRef<Promise<void>>(Promise.resolve())
  const onStateChangeRef = useRef(onStateChange)
  onStateChangeRef.current = onStateChange
  const stateRef = useRef(state)
  stateRef.current = state

  const teardown = useCallback(() => {
    mp4Ref.current = null
    compositorRef.current?.stop()
    compositorRef.current = null
    mixerRef.current?.close()
    mixerRef.current = null
    recorderRef.current = null
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
  }, [])

  /** Returns false when this path can't run, so start() can fall back. */
  const startWebCodecs = useCallback(async (
    tiles: CompositorTile[],
    audioStreams: MediaStream[],
    size: { width: number; height: number; fps: number },
  ): Promise<boolean> => {
    let file: { stream: { write(d: unknown): Promise<void>; close(): Promise<void> }; name: string } | null
    try {
      file = await createMp4FileStream(defaultRecordingName(new Date()))
    } catch {
      // The user dismissed the save dialog — not an error, and not a reason to
      // fall through to MediaRecorder and prompt them a second time.
      return true
    }

    const compositor = createCompositor(tiles, size)
    const mixer = createAudioMixer(audioStreams)

    try {
      const recorder = await createMp4Recorder({
        canvas: compositor.canvas,
        audioTrack: mixer?.track ?? null,
        width: size.width,
        height: size.height,
        fps: size.fps,
        fileStream: file?.stream ?? null,
      })
      compositorRef.current = compositor
      mixerRef.current = mixer
      mp4Ref.current = recorder

      const startedAt = Date.now()
      timerRef.current = setInterval(() => {
        setState((st) => (st.isPaused ? st : { ...st, durationSeconds: Math.floor((Date.now() - startedAt) / 1000) }))
      }, 1000)

      const name = file?.name ?? `${defaultRecordingName(new Date())}.mp4`
      setState({
        isRecording: true, isPaused: false, durationSeconds: 0,
        fileName: name, error: null, playbackWarning: null,
      })
      onStateChangeRef.current?.(true)
      vclog('tile', 'local recording started (webcodecs mp4)', {
        ...size, streaming: !!file, tiles: tiles.length,
      })
      return true
    } catch (err) {
      compositor.stop()
      mixer?.close()
      try { await file?.stream.close() } catch { /* nothing written */ }
      vclog('tile', 'webcodecs recording unavailable, falling back', { error: (err as Error).message })
      return false
    }
  }, [])

  const start = useCallback(async ({ tiles, audioStreams }: StartArgs) => {
    if (recorderRef.current || mp4Ref.current) return

    // Deliberately a looser threshold than publishing uses: compositing every
    // tile and encoding the result is the heavier job. See device-capability.
    const dims = isWeakForRecording() ? WEAK : FULL

    // The container the user asked for in Settings, rather than always
    // preferring MP4. WebCodecs is the only path that writes a real seekable
    // MP4 — MediaRecorder only produces streaming containers, which is what
    // made recordings unseekable in Windows' default player.
    const container = useMeetingPreferencesStore.getState().recordingContainer
    if (container === 'video/mp4' && supportsWebCodecsRecording()) {
      const started = await startWebCodecs(tiles, audioStreams, dims)
      if (started) return
      // Fell through — MediaRecorder rather than failing outright. The
      // playbackWarning below then tells the user they got WebM after asking
      // for MP4, instead of leaving them to discover it when the file will not
      // seek.
      vclog('producer', 'MP4 requested but WebCodecs failed, using MediaRecorder', {})
    } else if (container === 'video/mp4') {
      vclog('producer', 'MP4 requested but WebCodecs is unsupported here', {})
    }

    const mimeType = pickRecordingMimeType()
    if (!mimeType) {
      setState((s) => ({ ...s, error: 'Recording is not supported in this browser.' }))
      return
    }

    let sink: RecordingSink
    try {
      // Must come first: showSaveFilePicker needs the user gesture that started
      // the recording, and a cancelled picker should abort before we spin up an
      // encoder and a compositor.
      sink = await createRecordingSink(defaultRecordingName(new Date()), mimeType)
    } catch {
      // AbortError — the user dismissed the save dialog. Not an error state.
      return
    }
    sinkRef.current = sink

    const size = dims

    try {
      const compositor = createCompositor(tiles, size)
      compositorRef.current = compositor

      const mixer = createAudioMixer(audioStreams)
      mixerRef.current = mixer

      const stream = new MediaStream(compositor.stream.getVideoTracks())
      if (mixer) stream.addTrack(mixer.track)

      const recorder = new MediaRecorder(stream, { mimeType })
      recorderRef.current = recorder

      recorder.ondataavailable = (event) => {
        if (!event.data || event.data.size === 0) return
        const sinkNow = sinkRef.current
        if (!sinkNow) return
        writeChainRef.current = writeChainRef.current
          .then(() => sinkNow.write(event.data))
          .catch((err) => {
            vclog('tile', 'recording write failed', { error: (err as Error).message })
            setState((s) => ({ ...s, error: 'Could not write to the recording file.' }))
          })
      }

      recorder.onerror = () => {
        setState((s) => ({ ...s, error: 'Recording stopped unexpectedly.' }))
      }

      recorder.start(TIMESLICE_MS)

      const startedAt = Date.now()
      timerRef.current = setInterval(() => {
        setState((s) => (s.isPaused ? s : { ...s, durationSeconds: Math.floor((Date.now() - startedAt) / 1000) }))
      }, 1000)

      setState({
        isRecording: true,
        isPaused: false,
        durationSeconds: 0,
        fileName: sink.name,
        error: null,
        playbackWarning: isFallbackWebm(mimeType)
          ? 'This browser can only record WebM. Windows Media Player may show no audio or refuse to seek — play it in VLC or a browser.'
          : null,
      })
      onStateChangeRef.current?.(true)
      // Log what we actually got, not what we asked for. Playback complaints
      // ("no audio", "won't seek", "stutters") are almost always the container,
      // and this line is what identifies it.
      vclog('tile', 'local recording started', {
        mimeType,
        ...size,
        streaming: supportsFileStreaming(),
        tiles: tiles.length,
        supported: supportedRecordingTypes(),
        webmFallback: isFallbackWebm(mimeType),
      })
    } catch (err) {
      await sink.abort().catch(() => {})
      sinkRef.current = null
      teardown()
      setState((s) => ({ ...s, isRecording: false, error: (err as Error).message || 'Could not start recording.' }))
    }
  }, [teardown])

  const stop = useCallback(async () => {
    const mp4 = mp4Ref.current
    if (mp4) {
      mp4Ref.current = null
      const { buffer } = await mp4.stop()
      const name = stateRef.current.fileName ?? 'meeting-recording.mp4'
      teardown()
      // Only the no-file-handle path hands back a buffer; the streaming one has
      // already written the file where the user chose.
      if (buffer) {
        const url = URL.createObjectURL(new Blob([buffer], { type: 'video/mp4' }))
        const a = document.createElement('a')
        a.href = url
        a.download = name
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      }
      setState({
        isRecording: false, isPaused: false, durationSeconds: 0,
        fileName: null, error: null, playbackWarning: null,
      })
      onStateChangeRef.current?.(false)
      vclog('tile', 'local recording stopped (webcodecs mp4)', { file: name })
      return
    }

    const recorder = recorderRef.current
    const sink = sinkRef.current
    if (!recorder || !sink) return

    await new Promise<void>((resolve) => {
      recorder.onstop = () => resolve()
      if (recorder.state !== 'inactive') recorder.stop()
      else resolve()
    })

    teardown()
    // The final chunks land in ondataavailable during stop(); close only after
    // they have all been written or the file is truncated.
    await writeChainRef.current
    const { downloadUrl } = await sink.close()
    sinkRef.current = null

    // Only the IndexedDB fallback hands back a Blob — the streaming sink has
    // already written the file where the user chose.
    if (downloadUrl) {
      const a = document.createElement('a')
      a.href = downloadUrl
      a.download = sink.name
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(downloadUrl)
    }

    setState({
      isRecording: false, isPaused: false, durationSeconds: 0,
      fileName: null, error: null, playbackWarning: null,
    })
    onStateChangeRef.current?.(false)
    vclog('tile', 'local recording stopped', { file: sink.name })
  }, [teardown])

  const pause = useCallback(() => {
    if (mp4Ref.current) {
      mp4Ref.current.pause()
      setState((s) => ({ ...s, isPaused: true }))
      return
    }
    const recorder = recorderRef.current
    if (recorder?.state !== 'recording') return
    recorder.pause()
    setState((s) => ({ ...s, isPaused: true }))
  }, [])

  const resume = useCallback(() => {
    if (mp4Ref.current) {
      mp4Ref.current.resume()
      setState((s) => ({ ...s, isPaused: false }))
      return
    }
    const recorder = recorderRef.current
    if (recorder?.state !== 'paused') return
    recorder.resume()
    setState((s) => ({ ...s, isPaused: false }))
  }, [])

  /** Keep the composite and the audio mix in step with the meeting. */
  const update = useCallback(({ tiles, audioStreams }: StartArgs) => {
    compositorRef.current?.setTiles(tiles)
    mixerRef.current?.setSources(audioStreams)
  }, [])

  // Closing the tab mid-recording loses everything after the last flushed chunk.
  useEffect(() => {
    if (!state.isRecording) return
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [state.isRecording])

  // Unmount (leaving the meeting) must finalise rather than drop the file.
  const stopRef = useRef(stop)
  stopRef.current = stop
  useEffect(() => {
    return () => {
      if (recorderRef.current || mp4Ref.current) void stopRef.current()
    }
  }, [])

  return {
    ...state,
    formattedDuration: formatDuration(state.durationSeconds),
    start,
    stop,
    pause,
    resume,
    update,
  }
}

function formatDuration(seconds: number): string {
  const hrs = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60
  if (hrs > 0) return `${hrs}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
}
